import React from "react";
import { Alert, Box, Button, Link, Stack, Typography } from "@mui/material";

import MissingRetailerControl from "./MissingRetailerControl";
import { buildGatewayAdminRecordUrl } from "../../../services/missingReports";

const MissingRetailerField = ({
  retailerLabel,
  retailerId,
  suggestedRetailerId,
  suggestedRetailerName,
  retailerResolutionSource,
  hasRetailerRefId,
  exactRetailerMatchFound,
  retailerMode,
  onRetailerModeChange,
  retailerName,
  onRetailerNameChange,
  onRetailerIdChange,
}) => {
  const sourceRetailerLabel = retailerLabel || "Unknown retailer provided";
  const selectedRetailerLabel = retailerName?.trim() || "";
  const sourceRetailerKey = sourceRetailerLabel.trim().toLowerCase();
  const selectedRetailerKey = selectedRetailerLabel.toLowerCase();
  const hasSuggestedRetailer = Boolean(suggestedRetailerId);
  const isUserOverrideSelection =
    retailerMode === "lookup" &&
    Boolean(retailerId) &&
    ((!hasSuggestedRetailer && Boolean(selectedRetailerLabel)) ||
      (hasSuggestedRetailer && retailerId !== suggestedRetailerId));
  const isDifferentFromSourceRetailer =
    Boolean(selectedRetailerKey) && selectedRetailerKey !== sourceRetailerKey;
  const showSelectedGatewayRetailer =
    isUserOverrideSelection && isDifferentFromSourceRetailer;
  const showResolvedGatewayRetailer = Boolean(retailerId) && !showSelectedGatewayRetailer;
  const resolvedFromRetailerRefId = retailerResolutionSource === "retailer_ref_id";
  const resolvedFromExactRawMatch =
    retailerResolutionSource === "exact_raw_retailer_match" ||
    retailerResolutionSource === "retailer_ref_id_unresolved_exact_raw_retailer_match";
  const resolvedGatewayRetailerLabel = selectedRetailerLabel || "Selected Gateway retailer";

  return (
    <Stack spacing={0.75}>
      <Typography variant="body2">
        <strong>Queue retailer text:</strong> {sourceRetailerLabel}
      </Typography>
      {showResolvedGatewayRetailer && resolvedFromRetailerRefId ? (
        <Typography variant="body2" color="text.secondary">
          <strong>Gateway retailer:</strong> {resolvedGatewayRetailerLabel}
        </Typography>
      ) : null}
      {showResolvedGatewayRetailer && resolvedFromExactRawMatch ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Found exact existing Gateway retailer match and preselected it: {resolvedGatewayRetailerLabel}.
          Using this avoids creating a duplicate retailer record.
        </Alert>
      ) : null}
      {hasRetailerRefId && !retailerId ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          This row has a `retailer_ref_id`, but no matching Gateway retailer was found.
          Choose an existing retailer before resolving.
        </Alert>
      ) : null}
      {showSelectedGatewayRetailer ? (
        <Typography variant="body2" color="text.secondary">
          <strong>Selected Gateway retailer:</strong> {selectedRetailerLabel}
        </Typography>
      ) : null}
    {retailerId ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Link
          href={buildGatewayAdminRecordUrl("retailer", retailerId)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          Open retailer in Gateway
        </Link>
        <Button
          variant="outlined"
          size="small"
          sx={{ minWidth: 130, whiteSpace: "nowrap" }}
          onClick={() => {
            onRetailerIdChange("");
            onRetailerModeChange("lookup");
          }}
        >
          Lookup retailer
        </Button>
      </Box>
    ) : null}
    {!retailerId ? (
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
          Select an existing Gateway retailer to continue.
        </Typography>
        {hasSuggestedRetailer ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            We found a suggested existing Gateway retailer
            {suggestedRetailerName ? ` (${suggestedRetailerName})` : ""}.
            Use lookup to confirm or pick another existing retailer.
          </Alert>
        ) : null}
        <MissingRetailerControl
          retailerMode={retailerMode}
          onRetailerModeChange={onRetailerModeChange}
          retailerName={retailerName}
          onRetailerNameChange={onRetailerNameChange}
          retailerId={retailerId}
          onRetailerIdChange={onRetailerIdChange}
        />
      </Stack>
    ) : null}
    </Stack>
  );
};

export default MissingRetailerField;
